import type { StepDefinition } from './workflowParser';
import { getActionStrategy } from './actionStrategies';
import { getConditionStrategy } from './conditionStrategies';

/** Max length for rule name in the automation editor. */
export const RULE_NAME_MAX_LENGTH = 256;
export const WORKFLOW_NAME_MAX_LENGTH = RULE_NAME_MAX_LENGTH;

export function clampWorkflowName(name: string): string {
    if (name.length <= RULE_NAME_MAX_LENGTH) return name;
    return name.slice(0, RULE_NAME_MAX_LENGTH);
}

function isStepPlaceholder(step: StepDefinition): boolean {
    if (step.type === 'trigger') return false;
    return step.data.isPlaceholder ?? false;
}

function getStepTitle(step: StepDefinition): string {
    if (step.type === 'trigger') return 'Trigger';
    return step.data.title ?? (step.type === 'condition' ? 'Condition' : 'Action');
}

export interface ValidationError {
    stepId: string;
    stepTitle: string;
    message: string;
}

/**
 * Validates steps before serialization.
 * Returns an empty array if everything is valid.
 */
export function validateWorkflow(steps: StepDefinition[], workflowName: string): ValidationError[] {
    const errors: ValidationError[] = [];

    if (!workflowName.trim()) {
        errors.push({ stepId: '', stepTitle: 'Rule', message: 'Rule name is required.' });
    } else if (workflowName.length > RULE_NAME_MAX_LENGTH) {
        errors.push({
            stepId: '',
            stepTitle: 'Rule',
            message: `Rule name must be ${RULE_NAME_MAX_LENGTH} characters or fewer.`,
        });
    }

    const configuredSteps = steps.filter((s) => s.type !== 'trigger');

    if (configuredSteps.length === 0) {
        errors.push({
            stepId: '',
            stepTitle: 'Rule',
            message: 'Add at least one condition or action to save this rule.',
        });
        return errors;
    }

    for (const step of configuredSteps) {
        if (isStepPlaceholder(step)) {
            errors.push({
                stepId: step.id,
                stepTitle: getStepTitle(step),
                message: `Unconfigured ${step.type} step — select a type or remove it.`,
            });
            continue;
        }

        if (step.type === 'condition') {
            errors.push(...validateConditionStep(step));
        } else {
            errors.push(...validateActionStep(step));
        }
    }

    return errors;
}

function validateConditionStep(step: Extract<StepDefinition, { type: 'condition' }>): ValidationError[] {
    const { data } = step;
    const title = data.title ?? 'Condition';
    const strategy = getConditionStrategy(data.conditionCode);
    return strategy ? strategy.validate(data, step.id, title) : [];
}

function validateActionStep(step: Extract<StepDefinition, { type: 'action' }>): ValidationError[] {
    const { data } = step;
    const title = data.title ?? 'Action';

    if (!data.actionCode && !data.actionType) {
        return [{ stepId: step.id, stepTitle: title, message: 'Select an action type.' }];
    }

    const strategy = getActionStrategy(data.actionCode);
    return strategy ? strategy.validate(data, step.id, title) : [];
}
